import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Loader2, Trash2, ExternalLink, Info, PlayCircle } from 'lucide-react';
import { apiRequest } from '@/utils/api-request';
import { showToast } from '@/components/ui/Toast';

interface VideosData {
    videos: Array<{
        asset_id: number;
        asset_type: number;
        asset: string;
    }>;
}

const Videos: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<VideosData | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [videoUrl, setVideoUrl] = useState('');
    const [addingVideo, setAddingVideo] = useState(false);
    const [deletingItem, setDeletingItem] = useState<number | null>(null);

    useEffect(() => {
        fetchVideosData();
    }, [id]);

    const fetchVideosData = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiRequest(`/media/${id}`);
            if (response.success && response.data && response.data.gallery) {
                setData({ videos: response.data.gallery.videos });
            } else {
                setError(response.message || 'Failed to load videos information');
            }
        } catch (err: any) {
            console.error('Error fetching videos data:', err);
            setError('Failed to load videos information');
        } finally {
            setLoading(false);
        }
    };

    const handleAddVideo = async () => {
        if (!videoUrl.trim()) {
            showToast('Please enter a video URL', 'error');
            return;
        }

        try {
            setAddingVideo(true);
            setError(null);

            const response = await apiRequest(`/media/${id}/gallery/video`, {
                method: 'POST',
                body: { url: videoUrl },
            });

            if (response.success) {
                showToast('Video URL added successfully!', 'success');
                await fetchVideosData();
                setVideoUrl('');
            } else {
                throw new Error(response.message || 'Failed to add video URL');
            }
        } catch (err: any) {
            console.error('Error adding video URL:', err);
            const errorMessage = err.message || 'Failed to add video URL';
            setError(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setAddingVideo(false);
        }
    };

    const handleDeleteVideo = async (assetId: number) => {
        if (!confirm('Are you sure you want to delete this video?')) return;

        try {
            setDeletingItem(assetId);
            setError(null);

            const response = await apiRequest(`/media/${id}/gallery/${assetId}`, {
                method: 'DELETE',
            });

            if (response.success) {
                showToast('Video deleted successfully!', 'success');
                await fetchVideosData();
            } else {
                throw new Error(response.message || 'Failed to delete video');
            }
        } catch (err: any) {
            console.error('Error deleting video:', err);
            const errorMessage = err.message || 'Failed to delete video';
            setError(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setDeletingItem(null);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader2 className="h-8 w-8 text-brandColor animate-spin" />
            </div>
        );
    }

    if (error && !data) {
        return (
            <div className="p-8">
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-red-800 dark:text-red-200">{error}</p>
                </div>
            </div>
        );
    }

    if (!data) {
        return (
            <div className="p-8">
                <p className="text-gray-500">No data available</p>
            </div>
        );
    }

    return (
        <div className="h-full bg-white dark:bg-gray-900">
            {/* Page Header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-8 py-4">
                <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                        <div className="p-2 bg-brandColor rounded-lg text-white">
                            <PlayCircle className="w-6 h-6" />
                        </div>
                    </div>
                    <div className="flex-1 min-w-0">
                        <h1 className="text-lg font-semibold text-gray-900 dark:text-white">School Videos</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Add video URLs from YouTube, Vimeo, or any other platform</p>
                    </div>
                </div>
            </div>

            {/* Auto-save notice */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border-b border-blue-200 dark:border-blue-800 px-8 py-3">
                <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                        Video URLs are saved immediately when you click "Add Video" button
                    </p>
                </div>
            </div>

            {error && (
                <div className="mx-8 mt-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                    <p className="text-red-800 dark:text-red-200">{error}</p>
                </div>
            )}

            <div className="p-8 space-y-8">
                {/* Video URLs */}
                <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="bg-brandColor px-6 py-3">
                        <h2 className="text-lg font-semibold text-white">Add Video URL</h2>
                        <p className="text-xs text-white/80 mt-1">Add video URLs from YouTube, Vimeo, or any other platform</p>
                    </div>
                    <div className="p-6 space-y-4">
                        {/* Add Video URL */}
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={videoUrl}
                                onChange={(e) => setVideoUrl(e.target.value)}
                                placeholder="Enter video URL (YouTube, Vimeo, etc.)"
                                className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        handleAddVideo();
                                    }
                                }}
                            />
                            <button
                                onClick={handleAddVideo}
                                disabled={addingVideo || !videoUrl.trim()}
                                className="inline-flex items-center px-4 py-2 bg-brandColor hover:bg-brand-hover text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {addingVideo ? (
                                    <>
                                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                        Adding...
                                    </>
                                ) : (
                                    <>
                                        <Plus className="w-5 h-5 mr-2" />
                                        Add Video
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Video List */}
                {data.videos.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="bg-brandColor px-6 py-3">
                            <h2 className="text-lg font-semibold text-white">School Videos ({data.videos.length})</h2>
                        </div>
                        <div className="p-6">
                            <div className="space-y-2">
                                {data.videos.map((video) => (
                                    <div
                                        key={video.asset_id}
                                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-brandColor transition-colors"
                                    >
                                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                                            <ExternalLink className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                            <a
                                                href={video.asset}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-sm text-brandColor hover:underline truncate"
                                            >
                                                {video.asset}
                                            </a>
                                        </div>
                                        <button
                                            onClick={() => handleDeleteVideo(video.asset_id)}
                                            disabled={deletingItem === video.asset_id}
                                            className="ml-2 p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                                        >
                                            {deletingItem === video.asset_id ? (
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                            ) : (
                                                <Trash2 className="w-4 h-4" />
                                            )}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}

                {data.videos.length === 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                        <div className="bg-brandColor px-6 py-3">
                            <h2 className="text-lg font-semibold text-white">School Videos (0)</h2>
                        </div>
                        <div className="p-6">
                            <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                                No videos added yet. Use the form above to add video URLs.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Videos;
